import {
  ElementCategoryID,
  IElementCategory,
} from '@shared/models/element-categories.model';
import { commonElementCategoryProperties } from '../common-element-category-properties.model';
import {
  DEFAULT_AREAL_DENSITY_PROPERTIES,
  DEFAULT_QUANTITY_PROPERTIES,
} from '@shared/models/element-quantities.model';
import { reusedContentProperty } from '../reused-content-property.model';
import {
  getElementPropertySelectionByValue,
  getValueFromConditionalNodes,
} from '@shared/helpers/conditional-nodes.helpers';
import { concreteProperties } from './concrete-properties';
import { concreteProductNodes } from './concrete-products';
import { ElementKind } from '@shared/models/project.model';
import {
  ConcreteProducts,
  ConcretePropertyName,
  ConcreteType,
} from './concrete.model';
import { getElementPropertyResolvedCountByNameOrId } from '@shared/helpers/element-property.helpers';
import { isOneOf } from '@shared/helpers/array.helpers';
import { getSbefCodeForProductElement } from '@shared/helpers/sbef.helpers';

const readyMixConreteDensity = 'densityArray[1]';
const steelRebarDensity = 'densityArray[2]';
const castInPlaceDensity = `${ConcretePropertyName.ReinforcementSteel} * (1 - ${readyMixConreteDensity}/${steelRebarDensity}) + ${readyMixConreteDensity}`;
const otherTypeDesity = `densityArray[1]`;
export const concreteDensity = `${ConcretePropertyName.Type} == "${ConcreteType.CastInPlace}" ? ${castInPlaceDensity} : ${otherTypeDesity}`;

/**
 * B1.1 carbonation: concrete reabsorbs CO2 during the use stage (kg CO2e per kg concrete).
 * Source: IVL, see DEV-3320.
 */
export const CONCRETE_CARBONATION_CO2E_PER_KG = -0.002;

const reinforcementSteelProducts = [
  ConcreteProducts.ReinforcementSteel,
  ConcreteProducts.ReinforcementSteelStainless,
] as const;

/** SBEF code for facades — concrete facades actively set B2 to 0 (IVL, DEV-3322) */
const FACADE_SBEF_CODE = '53';

/**
 * Add B1.1 carbonation uptake to concrete product results.
 * Applies to the concrete mass only — reinforcement steel is excluded.
 * Concrete facades (SBEF 53) also actively set B2 maintenance to 0.
 */
const mapResults: IElementCategory['mapResults'] = (results, context) => {
  // Raw read instead of getGenericProductId, which throws for custom products
  const genericProductId =
    context.productElement.generic_product_id ??
    context.productElement.product_id;
  if (isOneOf(reinforcementSteelProducts, genericProductId)) return results;

  const isFacade =
    getSbefCodeForProductElement(context.productElement, context.path) ===
    FACADE_SBEF_CODE;
  const maintenanceResults = isFacade ? { co2e_B2: 0 } : undefined;

  const concreteMass = results.kg ?? 0;
  if (!concreteMass) return { ...results, ...maintenanceResults };

  return {
    ...results,
    ...maintenanceResults,
    'co2e_B1.1': concreteMass * CONCRETE_CARBONATION_CO2E_PER_KG,
  };
};

export const concrete: IElementCategory = {
  id: ElementCategoryID.Concrete,
  name: 'Concrete',
  color: '#91A6CE',
  defaultSelectedQuantity: 'mass',
  mapResults,
  properties: [
    ...concreteProperties,
    reusedContentProperty,
    ...commonElementCategoryProperties,
  ],
  getQuantityProperties: () => {
    return {
      ...DEFAULT_QUANTITY_PROPERTIES,
      ...DEFAULT_AREAL_DENSITY_PROPERTIES,
      density: {
        fallbackCount: concreteDensity,
      },
    };
  },
  getElementPropertySelectionByProductId: (id) => {
    return getElementPropertySelectionByValue(id, concreteProductNodes);
  },
  getChildElements: (element) => {
    const type = getElementPropertyResolvedCountByNameOrId(
      element,
      ConcretePropertyName.Type,
    );

    // `throwIfNotFound: false` + `undefined` return: tolerate transient
    // Select counts (proposal edits can briefly leave
    // `ConcretePropertyName.Type` in a value that no longer matches the
    // conditional tree). Returning `undefined` skips enrichment entirely so
    // existing children are preserved until the state becomes valid again.
    const conditionalProductId = getValueFromConditionalNodes(
      element,
      [concreteProductNodes],
      false,
    );
    if (!conditionalProductId) return undefined;

    const concreteProduct = {
      kind: ElementKind.Product as const,
      product_id: conditionalProductId,
      count:
        type === ConcreteType.CastInPlace
          ? `max(0, mass - ${ConcretePropertyName.ReinforcementSteel} * volume)`
          : `mass`,
    };

    // Prefab types include reinforcement in the concrete product. A separate
    // rebar child would keep the cast-in-place kg after the property is removed
    // and the side panel would price that kg with the rebar factor (DEV-3915).
    if (type !== ConcreteType.CastInPlace) return [concreteProduct];

    const isStainless = getElementPropertyResolvedCountByNameOrId(
      element,
      ConcretePropertyName.StainlessReinforcement,
    );
    const steelProductId = isStainless
      ? ConcreteProducts.ReinforcementSteelStainless
      : ConcreteProducts.ReinforcementSteel;

    return [
      concreteProduct,
      {
        kind: ElementKind.Product,
        product_id: steelProductId,
        hide_product_without_count: true,
        count: `volume * ${ConcretePropertyName.ReinforcementSteel}`,
      },
    ];
  },
};
